import React, { useState, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, ArrowLeft, Filter, X } from 'lucide-react';
import { Layout } from '@/components/layout';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import SEO from '@/components/shared/SEO';

// Portfolio Projects Data
const portfolioProjects = [
  {
    id: 'aubuchon-hardware',
    title: 'Aubuchon Hardware',
    industry: 'Retail',
    category: 'installations',
    location: 'Multiple Locations',
    scope: 'Commercial flooring systems and entry solutions',
    description: 'Commercial flooring systems, durable retail-ready surfaces, and entry/transition solutions across multiple hardware store locations.',
    image: '/Assets/images/case-studies/aubuchon-hardware-hero.jpg',
    stats: { locations: 'Multi', sqft: 'Multi-location', timeline: 'Ongoing' }
  },
  {
    id: 'sky-zone',
    title: 'Sky Zone, Greenville SC',
    industry: 'Commercial',
    category: 'installations',
    location: 'Greenville, SC',
    scope: 'Recycled Rubber flooring installation with floor preparation',
    description: 'Recycled Rubber flooring installation with commercial-grade adhesives and floor preparation for high-traffic entertainment facility.',
    image: '/Assets/images/case-studies/sky-zone-hero.jpg',
    stats: { locations: '1', sqft: 'Full facility', timeline: 'Completed' }
  }
];

// Filter Options
const industries = ['All', 'Retail', 'Corporate', 'Healthcare', 'Hospitality', 'Sports & Entertainment', 'Grocery & Convenience'];
const categories = [
  { value: 'all', label: 'All Projects' },
  { value: 'logo-mats', label: 'Logo Mats' },
  { value: 'flooring', label: 'Custom Flooring' },
  { value: 'installations', label: 'Installations' }
];

const CustomPortfolioPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeIndustry, setActiveIndustry] = useState(searchParams.get('industry') || 'All');
  const [activeCategory, setActiveCategory] = useState(searchParams.get('category') || 'all');
  const [showFilters, setShowFilters] = useState(false);

  // Filter projects
  const filteredProjects = useMemo(() => {
    return portfolioProjects.filter(project => {
      const industryMatch = activeIndustry === 'All' || project.industry === activeIndustry;
      const categoryMatch = activeCategory === 'all' || project.category === activeCategory;
      return industryMatch && categoryMatch;
    });
  }, [activeIndustry, activeCategory]);

  const handleIndustryChange = (industry) => {
    setActiveIndustry(industry);
    const params = new URLSearchParams(searchParams);
    if (industry === 'All') {
      params.delete('industry');
    } else {
      params.set('industry', industry);
    }
    setSearchParams(params);
  };

  const handleCategoryChange = (category) => {
    setActiveCategory(category);
    const params = new URLSearchParams(searchParams);
    if (category === 'all') {
      params.delete('category');
    } else {
      params.set('category', category);
    }
    setSearchParams(params);
  };

  const clearFilters = () => {
    setActiveIndustry('All');
    setActiveCategory('all');
    setSearchParams({});
  };

  const hasActiveFilters = activeIndustry !== 'All' || activeCategory !== 'all';

  return (
    <Layout>
      <SEO 
        title="Project Portfolio"
        description="View our portfolio of successful commercial flooring projects. From national retail chains to corporate headquarters, see how Portico transforms spaces across industries."
        canonicalUrl="/custom/portfolio"
      />
      {/* Hero Section */}
      <section className="relative bg-primary-bg text-white py-16" data-testid="portfolio-hero">
        <div className="container-portico">
          <Link to="/custom" className="inline-flex items-center text-white/80 hover:text-white mb-6 transition-colors">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Custom Hub
          </Link>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <h1 className="font-heading text-4xl md:text-5xl font-bold mb-4">
              Portfolio
            </h1>
            <p className="text-xl text-white/90">
              Explore our gallery of successful custom branded environment projects across industries.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Filters */}
      <section className="py-6 bg-muted border-b border-border sticky top-0 z-40">
        <div className="container-portico">
          <div className="flex flex-wrap items-center justify-between gap-4">
            {/* Desktop Filters */}
            <div className="hidden md:flex items-center gap-6">
              {/* Category Filter */}
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-muted-foreground">Type:</span>
                <div className="flex gap-2">
                  {categories.map((cat) => (
                    <button
                      key={cat.value}
                      onClick={() => handleCategoryChange(cat.value)}
                      className={cn(
                        "px-3 py-1.5 text-sm rounded-full transition-colors",
                        activeCategory === cat.value
                          ? "bg-primary text-white"
                          : "bg-card border border-border hover:border-primary"
                      )}
                    >
                      {cat.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Industry Filter */}
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-muted-foreground">Industry:</span>
                <select
                  value={activeIndustry}
                  onChange={(e) => handleIndustryChange(e.target.value)}
                  className="px-3 py-1.5 text-sm rounded-md border border-border bg-card focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  {industries.map((industry) => (
                    <option key={industry} value={industry}>{industry}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Mobile Filter Toggle */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="md:hidden flex items-center gap-2 px-4 py-2 bg-card border border-border rounded-lg"
            >
              <Filter className="w-4 h-4" />
              Filters
              {hasActiveFilters && (
                <span className="w-2 h-2 rounded-full bg-primary" />
              )}
            </button>

            {/* Results Count & Clear */}
            <div className="flex items-center gap-4">
              <span className="text-sm text-muted-foreground">
                Showing <strong>{filteredProjects.length}</strong> projects
              </span>
              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="text-sm text-primary hover:underline flex items-center gap-1"
                >
                  <X className="w-3 h-3" />
                  Clear filters
                </button>
              )}
            </div>
          </div>

          {/* Mobile Filters Dropdown */}
          {showFilters && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden mt-4 pt-4 border-t border-border space-y-4"
            >
              <div>
                <span className="text-sm font-medium mb-2 block">Type</span>
                <div className="flex flex-wrap gap-2">
                  {categories.map((cat) => (
                    <button
                      key={cat.value}
                      onClick={() => handleCategoryChange(cat.value)}
                      className={cn(
                        "px-3 py-1.5 text-sm rounded-full transition-colors",
                        activeCategory === cat.value
                          ? "bg-primary text-white"
                          : "bg-card border border-border"
                      )}
                    >
                      {cat.label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <span className="text-sm font-medium mb-2 block">Industry</span>
                <select
                  value={activeIndustry}
                  onChange={(e) => handleIndustryChange(e.target.value)}
                  className="w-full px-3 py-2 text-sm rounded-md border border-border bg-card"
                >
                  {industries.map((industry) => (
                    <option key={industry} value={industry}>{industry}</option>
                  ))}
                </select>
              </div>
            </motion.div>
          )}
        </div>
      </section>

      {/* Portfolio Grid */}
      <section className="section-padding bg-background">
        <div className="container-portico">
          {filteredProjects.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredProjects.map((project, index) => (
                <motion.div
                  key={project.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Link 
                    to={`/custom/portfolio/${project.id}`}
                    className="group block"
                    data-testid={`portfolio-card-${project.id}`}
                  >
                    <div className="bg-card rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 border border-border hover:border-primary">
                      {/* Image */}
                      <div className="h-56 overflow-hidden relative">
                        <img
                          src={project.image}
                          alt={project.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                        <div className="absolute top-4 left-4">
                          <span className="px-3 py-1 bg-white/90 text-xs font-semibold rounded-full text-primary">
                            {project.industry}
                          </span>
                        </div>
                      </div>
                      
                      {/* Content */}
                      <div className="p-6">
                        <h3 className="font-heading font-bold text-lg mb-2 group-hover:text-primary transition-colors">
                          {project.title}
                        </h3>
                        <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                          {project.description}
                        </p>
                        
                        {/* Stats */}
                        <div className="grid grid-cols-3 gap-2 mb-4 text-center">
                          <div className="bg-muted rounded-lg p-2">
                            <div className="text-sm font-bold text-primary">{project.stats.locations}</div>
                            <div className="text-xs text-muted-foreground">Locations</div>
                          </div>
                          <div className="bg-muted rounded-lg p-2">
                            <div className="text-sm font-bold text-primary">{project.stats.sqft}</div>
                            <div className="text-xs text-muted-foreground">Sq Ft</div>
                          </div>
                          <div className="bg-muted rounded-lg p-2">
                            <div className="text-sm font-bold text-primary">{project.stats.timeline}</div>
                            <div className="text-xs text-muted-foreground">Timeline</div>
                          </div>
                        </div>

                        <div className="flex items-center text-sm font-semibold text-primary">
                          View Case Study <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                        </div>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <p className="text-lg text-muted-foreground mb-4">No projects match your current filters.</p>
              <Button variant="outline" onClick={clearFilters}>
                Clear Filters
              </Button>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-secondary text-secondary-foreground">
        <div className="container-portico text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-2xl mx-auto"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
              Ready to Start Your Project?
            </h2>
            <p className="text-lg text-secondary-foreground/80 mb-8">
              Let's discuss how we can create a custom solution for your space.
            </p>
            <Link to="/contact">
              <Button size="lg" className="bg-primary-bg hover:bg-primary-bg/90">
                Request Consultation
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
};

export default CustomPortfolioPage;
